import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import { Box } from "@mui/material";
import {
  BaseCard,
  Button,
  FieldsCard,
  FieldsData,
  paletteVar,
  Typography,
} from "@platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSignedInUser } from "@platform/user/client";

import { EditRfpCarriersDialog } from "../../../components/editRfpCarriers";
import { useAnalyticsData } from "../../../contexts/analyticsContext";
import {
  getRfpConnectedCarriersQueryKey,
  RFPConnectedCarrierResponse,
  RFPConnectedCarriersResponse,
  useRfpConnectedCarriers,
} from "../../../hooks/useRfpConnectedCarriers";

type Props = { rfpId: string; refetchRpfDetails: VoidFunction };

function RFPCarriersCard({
  rfpId,
  refetchRpfDetails,
}: Props): React.JSX.Element {
  const [editingCarriers, setEditingCarriers] = useState<string[] | null>(null);
  const { dispatch } = useAnalyticsData();
  const { isBrokerOrAgent } = useSignedInUser();
  const queryClient = useQueryClient();

  const queryKey = getRfpConnectedCarriersQueryKey(rfpId);
  const query = useRfpConnectedCarriers({
    rfpId,
    select: mapResponseToFieldsCardData,
    onSuccess: (data) => {
      dispatch({
        carrierConnectedNames: data.connected.map((carrier) => carrier.name),
        carrierConnectedCount: data.connected.length,
        carrierUnconnectedNames: data.unconnected.map(
          (carrier) => carrier.name,
        ),
        carrierUnconnectedCount: data.unconnected.length,
      });
    },
  });

  if (query.data?.centroSelectCarrierEnabled && !editingCarriers) {
    return (
      <BaseCard
        title="Carriers"
        action={
          <Button
            data-testid="carriers-edit"
            variant="tertiary"
            onClick={() => setEditingCarriers([])}
          >
            Edit
          </Button>
        }
      >
        <Typography>
          <em>Centro to select carriers</em>
        </Typography>
      </BaseCard>
    );
  }

  const handleEdit = isBrokerOrAgent
    ? undefined
    : () => {
        const data =
          queryClient.getQueryData<RFPConnectedCarriersResponse>(queryKey);
        const carriers = data
          ? [...data.connected, ...data.unconnected, ...data.unknown].map(
              (carrier) => carrier.name,
            )
          : [];
        setEditingCarriers(carriers);
      };

  return (
    <>
      <FieldsCard
        title="Carriers"
        subtitle={
          query.data?.total
            ? `${query.data?.total} Total Carriers to Quote`
            : null
        }
        status={query.status}
        error={query.error}
        gridItemProps={{
          size: 12,
        }}
        skeleton={{
          count: 3,
          props: {
            width: "50%",
            height: 30,
          },
        }}
        onEdit={handleEdit}
        data={query.data?.fields}
      />
      {editingCarriers ? (
        <EditRfpCarriersDialog
          onClose={() => setEditingCarriers(null)}
          onUpdateCarriers={() => {
            query.refetch();
            refetchRpfDetails();
          }}
          carriers={editingCarriers}
          rfpId={rfpId}
        />
      ) : null}
    </>
  );
}

type Data = {
  total: number;
  centroSelectCarrierEnabled: boolean;
  fields: FieldsData[];
};

function mapResponseToFieldsCardData(resp: RFPConnectedCarriersResponse): Data {
  const connected = resp.connected ?? [];
  const unconnected = resp.unconnected ?? [];
  const unknown = resp.unknown ?? [];
  const total = connected.length + unconnected.length + unknown.length;

  return {
    total,
    centroSelectCarrierEnabled:
      // if total is greater than 0 (carriers were selected), centro already selected carriers
      total > 0 ? false : resp.centroSelectCarrierEnabled,
    fields: [
      {
        label: {
          name: "Connected Carriers",
          icon: (
            <SyncOutlinedIcon
              fontSize="small"
              sx={{ color: paletteVar("secondary-main", "#4FB3CD") }}
            />
          ),
        },
        value: renderCarrierSalesRepEmails(connected),
      },
      {
        label: "Unconnected Carriers",
        value: renderCarrierSalesRepEmails(unconnected),
      },
      {
        label: "Unknown Carrier",
        value: renderCarrierSalesRepEmails(unknown),
      },
    ],
  };
}

function renderCarrierSalesRepEmails(
  carriers: RFPConnectedCarrierResponse[],
): string | React.JSX.Element {
  if (carriers.length === 0) {
    return "-";
  }

  return (
    <Box>
      {carriers.map((carrier) => {
        const salesRepEmailText =
          carrier.salesRepEmails?.length > 0
            ? carrier.salesRepEmails?.join(", ")
            : "not assigned";

        return (
          <Box key={carrier.name} sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
            <Typography variant="body-medium">{carrier.name}</Typography>
            <Typography
              variant="body-medium"
              color={
                carrier.salesRepEmails?.length > 0 ? "textSecondary" : "danger"
              }
            >
              Sales Rep: {salesRepEmailText}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default RFPCarriersCard;
